import {RefObject} from 'react';

import styled from 'styled-components';

import {media} from '~/shared/theme/media';
import {getLocalizationService} from '~/shared/services/localisationService';

import AmountHeadline from '../AmountHeadline';
import ConvertLoyaltyBalanceConfetti from '../ConvertLoyaltyBalanceConfetti';
import ConvertLoyaltyBalanceSuccessIllustration from '../ConvertLoyaltyBalanceSuccessIllustration';
import {ActionButton, Body, ButtonsContainer} from '../styled';

/* The illustration is a sibling in the flow rather than a backdrop, so all the body has to do is
   clear the gap the design leaves underneath it. */
const SuccessBody = styled(Body)`
  padding-top: 28px;
  ${media.minLargeMobile`
    padding-top: 54px;
  `}
`;

/* The design opens up more room above the action here than the rest of the flow does - on the sheet
   the inherited `margin-top: auto` already pins it to the bottom. */
const SuccessButtons = styled(ButtonsContainer)`
  ${media.minLargeMobile`
    margin-top: 77px;
  `}
`;

type SuccessScreenProps = {
  convertedAmount: number;
  onClose: () => void;
  /* The dialog the confetti breaks over - it is painted outside it, so it has to be measured. */
  dialogRef: RefObject<HTMLElement>;
};

/* Reports what landed in the user's credit. The headline reads title-first here, unlike the
   confirmation screen it follows, because the amount is the outcome rather than the thing being
   agreed to. */
const SuccessScreen = ({convertedAmount, onClose, dialogRef}: SuccessScreenProps) => {
  const {t} = getLocalizationService();

  return (
    <>
      {/* Mounts with the screen and plays itself out once, so arriving here is what sets it off. */}
      <ConvertLoyaltyBalanceConfetti anchorRef={dialogRef} />
      <ConvertLoyaltyBalanceSuccessIllustration />
      <SuccessBody data-test-id="convertLoyaltyBalanceModal_successScreen" role="status">
        <AmountHeadline
          amount={convertedAmount}
          subtitle={t('tap_and_save_convert_success_subtitle')}
          subtitleAbove
          data-test-id="convertLoyaltyBalanceModal_convertedAmount"
        />
        <SuccessButtons>
          <ActionButton
            variant="primaryLarge"
            data-test-id="convertLoyaltyBalanceModal_doneButton"
            onClick={onClose}
          >
            {t('close2')}
          </ActionButton>
        </SuccessButtons>
      </SuccessBody>
    </>
  );
};

export default SuccessScreen;
